import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowDown, ArrowUp, Eye, FileSignature, Plus, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';
import DocHtmlEditor, { DocHtmlEditorHandle } from '../components/DocHtmlEditor';
import PdfPreviewModal from '../components/PdfPreviewModal';

// Modello del verbale di onboarding per partner (2026-11-01, backend routes/onboardingTemplates.ts):
// intestazioni, loghi, fornitore, firmatari e testi delle sezioni. Vale per i clienti di quel
// partner; le bozze già scritte restano com'erano finché non si riallineano dal wizard. Un
// platform admin sceglie il partner dal menu o dal tenant in "Viewing", come in Report Templates.

interface Section { key: string; title: string; body: string; enabled: boolean }
interface Config {
  partnerName?: string; partnerSignatoryName?: string; partnerSignatoryRole?: string; attentionOf?: string; place?: string;
  providerName?: string; providerShortName?: string; providerAddress?: string[]; providerPhone?: string;
  authorName?: string; authorRole?: string; presentedByTeam?: string; documentTitle?: string; coverTitle?: string[]; coverTagline?: string[];
  socPhone?: string; socEmail?: string; changeRequestEmail?: string; reportCcEmails?: string[]; contractPrefix?: string;
  proprietaryNotice?: string; pecNote?: string; footerColumns?: string[][];
  headerLogoLeft?: string; headerLogoRight?: string; partnerLogo?: string; coverImage?: string;
  datacenter?: { name?: string; address?: string; vat?: string; certificationsImage?: string };
  sections?: Section[];
}
type TextKey = 'partnerName' | 'partnerSignatoryName' | 'partnerSignatoryRole' | 'attentionOf' | 'place' | 'providerName' | 'providerShortName' | 'providerPhone'
  | 'authorName' | 'authorRole' | 'presentedByTeam' | 'documentTitle' | 'socPhone' | 'socEmail' | 'changeRequestEmail' | 'contractPrefix';
type LinesKey = 'providerAddress' | 'coverTitle' | 'coverTagline' | 'reportCcEmails';
type ImageKey = 'headerLogoLeft' | 'headerLogoRight' | 'partnerLogo' | 'coverImage';

const MAX_IMAGE_BYTES = 500 * 1024;

const GROUPS: Array<{ title: string; fields: Array<[TextKey, string]>; lines?: Array<[LinesKey, string]> }> = [
  { title: 'Document', fields: [['documentTitle', 'Document title *'], ['place', 'Place of issue *'], ['contractPrefix', 'Contract code prefix (e.g. NGD)']], lines: [['coverTitle', 'Cover title (one line per row)'], ['coverTagline', 'Second page tagline (one line per row)']] },
  { title: 'Addressee', fields: [['partnerName', 'Partner name *'], ['partnerSignatoryName', 'Partner signatory'], ['partnerSignatoryRole', 'Signatory role'], ['attentionOf', 'For the attention of (default)']] },
  { title: 'Service provider', fields: [['providerName', 'Company name *'], ['providerShortName', 'Short name'], ['providerPhone', 'Phone'], ['authorName', 'Author and signatory *'], ['authorRole', 'Author role'], ['presentedByTeam', 'Presented by (second line)']], lines: [['providerAddress', 'Address (one line per row)']] },
  { title: 'SOC contacts', fields: [['socPhone', 'SOC phone *'], ['socEmail', 'SOC email *'], ['changeRequestEmail', 'Email for temporary contact changes']], lines: [['reportCcEmails', 'Report copy recipients (one per row)']] },
];

const IMAGES: Array<[ImageKey, string]> = [['partnerLogo', 'Partner logo (cover)'], ['headerLogoLeft', 'Page header, left'], ['headerLogoRight', 'Page header, right'], ['coverImage', 'Second page image']];

function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_IMAGE_BYTES) { reject(new Error('Images can be up to 500 KB')); return; }
    if (!/^image\/(png|jpeg)$/.test(file.type)) { reject(new Error('Use a PNG or JPEG image')); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the image'));
    reader.readAsDataURL(file);
  });
}

function ImageField({ label, preview, disabled, onPick, onClear, onError }: {
  label: string; preview?: string; disabled: boolean; onPick: (dataUrl: string) => void; onClear: () => void; onError: (message: string) => void;
}) {
  return (
    <div>
      <span className="block text-xs text-gray-500 mb-1">{label}</span>
      <div className="flex items-center gap-2 border border-gray-200 rounded p-2 min-h-[56px] bg-white">
        {preview ? <img src={preview} alt="" className="h-9 max-w-[120px] object-contain" /> : <span className="text-xs text-gray-400">No image</span>}
        {!disabled && (
          <span className="ml-auto flex items-center gap-1">
            <label className="btn-secondary text-xs cursor-pointer inline-flex items-center gap-1">
              <Upload size={12} /> Upload
              <input type="file" accept="image/png,image/jpeg" className="sr-only" onChange={async e => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                try { onPick(await readImage(file)); } catch (err) { onError((err as Error).message); }
              }} />
            </label>
            {preview && <button type="button" onClick={onClear} aria-label={`Remove ${label}`} className="p-1 text-gray-400 hover:text-red-600"><X size={14} /></button>}
          </span>
        )}
      </div>
    </div>
  );
}

export default function OnboardingTemplatePage() {
  const { user, isAdmin } = useAuth();
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const canManage = isAdmin && user?.scopeLevel !== 'tenant';
  const [partners, setPartners] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(user?.partnerId ?? null);
  const [tenantPartnerId, setTenantPartnerId] = useState<number | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [exists, setExists] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState<Config | null>(null);
  const [defaults, setDefaults] = useState<Section[]>([]);
  const [tokens, setTokens] = useState<Array<{ token: string; label: string }>>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [busy, setBusy] = useState<'save' | 'preview' | null>(null);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const editor = useRef<DocHtmlEditorHandle>(null);

  useEffect(() => {
    if (user?.scopeLevel === 'platform') {
      axios.get('/api/partners').then(res => {
        setPartners(res.data.data);
        if (!selectedPartnerId && res.data.data[0]) setSelectedPartnerId(res.data.data[0].id);
      });
    }
  }, [user]);

  useEffect(() => {
    if (!selectedTenantId) { setTenantPartnerId(null); return; }
    axios.get(`/api/tenants/${selectedTenantId}`).then(res => setTenantPartnerId(res.data.data.partner_id)).catch(() => setTenantPartnerId(null));
  }, [selectedTenantId]);

  const partnerId = selectedTenantId ? tenantPartnerId : selectedPartnerId;

  useEffect(() => {
    setConfig(null); setMessage(null); setDirty(false);
    if (!partnerId || user?.scopeLevel === 'tenant') return;
    axios.get(`/api/onboarding-templates/${partnerId}`).then(res => {
      const d = res.data.data;
      setPartnerName(d.partnerName); setExists(d.exists); setEnabled(d.exists ? d.enabled : true); setConfig(d.config);
      setDefaults(d.defaults); setTokens(d.tokens); setPreviews(d.imagePreviews || {});
      setSelected(d.config.sections?.[0]?.key ?? null); setEditorEpoch(e => e + 1);
    }).catch(err => setMessage({ ok: false, text: err.response?.data?.error || 'Could not load the template' }));
  }, [partnerId]);

  if (user?.scopeLevel === 'tenant') {
    return <div className="card p-6 text-sm text-gray-500">The onboarding document template is managed by your service provider.</div>;
  }

  const patch = (p: Partial<Config>) => { setConfig(c => ({ ...c, ...p })); setDirty(true); };
  const sections = config?.sections ?? [];
  const section = sections.find(s => s.key === selected) ?? null;
  const patchSection = (key: string, p: Partial<Section>) => patch({ sections: sections.map(s => (s.key === key ? { ...s, ...p } : s)) });

  function move(key: string, delta: number) {
    const i = sections.findIndex(s => s.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    patch({ sections: next });
  }

  function addSection() {
    const key = `custom_${Date.now().toString(36)}`;
    patch({ sections: [...sections, { key, title: 'New section', body: '<p></p>', enabled: true }] });
    setSelected(key); setEditorEpoch(e => e + 1);
  }

  function removeSection(key: string) {
    if (!window.confirm('Remove this section from the template?')) return;
    const next = sections.filter(s => s.key !== key);
    patch({ sections: next });
    setSelected(next[0]?.key ?? null); setEditorEpoch(e => e + 1);
  }

  function restoreSection(key: string) {
    const original = defaults.find(s => s.key === key);
    if (!original || !window.confirm('Replace this section with the default text?')) return;
    patchSection(key, { title: original.title, body: original.body });
    setEditorEpoch(e => e + 1);
  }

  async function save() {
    setBusy('save'); setMessage(null);
    try {
      const res = await axios.put(`/api/onboarding-templates/${partnerId}`, { config, enabled });
      setConfig(res.data.data.config); setPreviews(res.data.data.imagePreviews || {});
      setExists(true); setDirty(false); setEditorEpoch(e => e + 1);
      setMessage({ ok: true, text: 'Template saved. Existing drafts keep their text until they are updated from the wizard.' });
    } catch (err: any) {
      setMessage({ ok: false, text: err.response?.data?.error || 'Could not save the template' });
    } finally { setBusy(null); }
  }

  async function showPreview() {
    setBusy('preview'); setMessage(null);
    try {
      const res = await axios.post(`/api/onboarding-templates/${partnerId}/preview`, { config }, { responseType: 'blob' });
      setPreview(URL.createObjectURL(res.data));
    } catch (err: any) {
      // La risposta di errore arriva come blob, per via di responseType.
      let text = 'Could not generate the preview';
      try { text = JSON.parse(await err.response.data.text()).error || text; } catch { /* resta il testo generico */ }
      setMessage({ ok: false, text });
    } finally { setBusy(null); }
  }

  const ro = !canManage;
  const field = 'input-field w-full text-sm';
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[260px]">
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2"><FileSignature size={22} className="text-primary-600" /> Onboarding Document</h1>
          <p className="text-sm text-gray-500 mt-1">The template of the onboarding document ("verbale") that this partner issues to its customers.</p>
        </div>
        {selectedTenantId ? (
          <p className="text-sm text-gray-600 self-center">{partnerName} · system integrator of {selectedTenantName}</p>
        ) : user?.scopeLevel === 'platform' && (
          <label className="text-xs text-gray-500">Partner
            <select className="input-field block text-sm" value={selectedPartnerId ?? ''} onChange={e => setSelectedPartnerId(Number(e.target.value))}>
              {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {message && <p role="status" className={`text-sm ${message.ok ? 'text-green-700' : 'text-red-700'}`}>{message.text}</p>}

      {config && (
        <>
          {!exists && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{partnerName} has no template yet, so its customers cannot get an onboarding document. Fill in the fields marked * and save to enable it.</div>}

          <div className="card p-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {GROUPS.map(group => (
                <fieldset key={group.title} disabled={ro} className="space-y-2">
                  <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">{group.title}</legend>
                  {group.fields.map(([key, label]) => (
                    <label key={key} className="block text-xs text-gray-500">{label}
                      <input className={field} value={config[key] ?? ''} onChange={e => patch({ [key]: e.target.value })} />
                    </label>
                  ))}
                  {group.lines?.map(([key, label]) => (
                    <label key={key} className="block text-xs text-gray-500">{label}
                      <textarea className={field} rows={2} value={(config[key] ?? []).join('\n')} onChange={e => patch({ [key]: e.target.value.split('\n') })} />
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>

            <div>
              <h2 className="text-xs font-semibold text-gray-500 uppercase mb-2">Images</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {IMAGES.map(([key, label]) => (
                  <ImageField key={key} label={label} preview={previews[key]} disabled={ro}
                    onError={text => setMessage({ ok: false, text })}
                    onPick={url => { patch({ [key]: url }); setPreviews(p => ({ ...p, [key]: url })); }}
                    onClear={() => { patch({ [key]: undefined }); setPreviews(({ [key]: _gone, ...rest }) => rest); }} />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <fieldset disabled={ro} className="space-y-2">
                <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">Datacenter (log retention section)</legend>
                <input aria-label="Datacenter name" className={field} placeholder="Name" value={config.datacenter?.name ?? ''} onChange={e => patch({ datacenter: { ...config.datacenter, name: e.target.value } })} />
                <input aria-label="Datacenter address" className={field} placeholder="Address" value={config.datacenter?.address ?? ''} onChange={e => patch({ datacenter: { ...config.datacenter, address: e.target.value } })} />
                <input aria-label="Datacenter VAT number" className={field} placeholder="VAT number" value={config.datacenter?.vat ?? ''} onChange={e => patch({ datacenter: { ...config.datacenter, vat: e.target.value } })} />
                <ImageField label="Certifications image" preview={previews.certificationsImage} disabled={ro}
                  onError={text => setMessage({ ok: false, text })}
                  onPick={url => { patch({ datacenter: { ...config.datacenter, certificationsImage: url } }); setPreviews(p => ({ ...p, certificationsImage: url })); }}
                  onClear={() => { patch({ datacenter: { ...config.datacenter, certificationsImage: undefined } }); setPreviews(({ certificationsImage: _gone, ...rest }) => rest); }} />
              </fieldset>
              <fieldset disabled={ro} className="space-y-2">
                <legend className="text-xs font-semibold text-gray-500 uppercase mb-1">Legal text and page footer</legend>
                <label className="block text-xs text-gray-500">Proprietary notice (cover)
                  <textarea className={field} rows={3} value={config.proprietaryNotice ?? ''} onChange={e => patch({ proprietaryNotice: e.target.value })} />
                </label>
                <label className="block text-xs text-gray-500">Closing note (after the signatures)
                  <textarea className={field} rows={2} value={config.pecNote ?? ''} onChange={e => patch({ pecNote: e.target.value })} />
                </label>
                <span className="block text-xs text-gray-500">Page footer, up to 4 columns (one line per row)</span>
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1, 2, 3].map(i => (
                    <textarea key={i} aria-label={`Footer column ${i + 1}`} className={field} rows={3} value={(config.footerColumns?.[i] ?? []).join('\n')} onChange={e => {
                      const cols = [0, 1, 2, 3].map(n => config.footerColumns?.[n] ?? []);
                      cols[i] = e.target.value.split('\n');
                      patch({ footerColumns: cols });
                    }} />
                  ))}
                </div>
              </fieldset>
            </div>
          </div>

          <div className="card p-4 space-y-3">
            <div>
              <h2 className="font-medium text-gray-900">Sections</h2>
              <p className="text-xs text-gray-500">Numbering and index follow the enabled sections. Placeholders such as {'{{contract}}'} are replaced with the customer's data when a draft is written. Signatures always close the last section.</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-3">
              <nav aria-label="Sections" className="lg:w-64 shrink-0 space-y-0.5">
                {sections.map((s, i) => (
                  <div key={s.key} className={`flex items-center gap-1.5 rounded px-2 py-1 text-sm ${s.key === selected ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                    <input type="checkbox" disabled={ro} aria-label={`Enable ${s.title}`} checked={s.enabled} onChange={e => patchSection(s.key, { enabled: e.target.checked })} />
                    <button onClick={() => { setSelected(s.key); setEditorEpoch(e => e + 1); }} className={`flex-1 min-w-0 text-left truncate ${s.enabled ? 'text-gray-800' : 'text-gray-400 line-through'}`} title={s.title}>{s.title}</button>
                    {!ro && <>
                      <button disabled={i === 0} onClick={() => move(s.key, -1)} aria-label={`Move ${s.title} up`} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowUp size={12} /></button>
                      <button disabled={i === sections.length - 1} onClick={() => move(s.key, 1)} aria-label={`Move ${s.title} down`} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ArrowDown size={12} /></button>
                    </>}
                  </div>
                ))}
                {!ro && <button onClick={addSection} className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1 px-2 py-1"><Plus size={13} /> Add section</button>}
              </nav>
              <div className="flex-1 min-w-0 space-y-2">
                {section && (
                  <>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="block text-xs text-gray-500 flex-1 min-w-[200px]">Section title
                        <input disabled={ro} className={field} maxLength={120} value={section.title} onChange={e => patchSection(section.key, { title: e.target.value })} />
                      </label>
                      {!ro && defaults.some(d => d.key === section.key) && <button onClick={() => restoreSection(section.key)} className="btn-secondary text-sm inline-flex items-center gap-1"><RotateCcw size={13} /> Default text</button>}
                      {!ro && <button onClick={() => removeSection(section.key)} className="btn-secondary text-sm text-red-700 inline-flex items-center gap-1"><Trash2 size={13} /> Remove</button>}
                    </div>
                    <DocHtmlEditor ref={editor} key={`${section.key}|${editorEpoch}`} html={section.body} disabled={ro} onChange={body => patchSection(section.key, { body })} />
                    {!ro && (
                      <label className="block text-xs text-gray-500">Insert a placeholder
                        <select className="input-field block text-sm" value="" onChange={e => { if (e.target.value) editor.current?.insertText(`{{${e.target.value}}}`); }}>
                          <option value="">Choose…</option>
                          {tokens.map(t => <option key={t.token} value={t.token}>{t.label}</option>)}
                        </select>
                      </label>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="card p-3 flex flex-wrap items-center gap-3 sticky bottom-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" disabled={ro} checked={enabled} onChange={e => { setEnabled(e.target.checked); setDirty(true); }} /> Available to this partner's customers
            </label>
            <span className="text-xs text-gray-500 mr-auto">{dirty ? 'Unsaved changes' : ''}</span>
            <button onClick={showPreview} disabled={busy !== null} className="btn-secondary inline-flex items-center gap-1"><Eye size={13} /> {busy === 'preview' ? 'Generating…' : 'Preview with sample data'}</button>
            {canManage && <button onClick={save} disabled={busy !== null} className="btn-primary">{busy === 'save' ? 'Saving…' : 'Save template'}</button>}
          </div>
        </>
      )}

      {preview && <PdfPreviewModal url={preview} title="Template preview with sample data" onClose={() => setPreview(null)} />}
    </div>
  );
}
